export const MAX_IMAGE_UPLOAD_MB = 12; // 12MB
export const TARGET_IMAGE_MB = 4; // 4MB

export const MAX_IMAGE_UPLOAD_BYTES = MAX_IMAGE_UPLOAD_MB * 1024 * 1024;
export const TARGET_IMAGE_BYTES = TARGET_IMAGE_MB * 1024 * 1024;

const createImageFromBlob = async (blob: Blob): Promise<HTMLImageElement> => {
    const url = URL.createObjectURL(blob);

    try {
        const image = new Image();
        image.decoding = 'async';

        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve();
            image.onerror = error => reject(error);
            image.src = url;
        });

        return image;
    } finally {
        URL.revokeObjectURL(url);
    }
};

const blobToFile = (blob: Blob, fileName: string, fallbackType?: string): File => {
    const type = blob.type || fallbackType || 'image/jpeg';
    return new File([blob], fileName, { type });
};

type ResizeOptions = {
    targetMb?: number;
    targetBytes?: number;
    outputMimeType?: string;
    quality?: number;
    maxIterations?: number;
    minScale?: number;
};

export const resizeBlobToMaxBytes = async (blob: Blob, fileName: string, options?: ResizeOptions) => {
    const targetBytes = options?.targetBytes ?? (options?.targetMb ?? TARGET_IMAGE_MB) * 1024 * 1024;
    const maxIterations = options?.maxIterations ?? 6;
    const minScale = options?.minScale ?? 0.12;
    const quality = options?.quality ?? 0.92;

    if (blob.size <= targetBytes) {
        return blobToFile(blob, fileName, blob.type);
    }

    const originalMimeType = blob.type;
    const outputMimeType =
        options?.outputMimeType ??
        (originalMimeType === 'image/png' || originalMimeType === 'image/x-png'
            ? 'image/jpeg'
            : originalMimeType || 'image/jpeg');

    const image = await createImageFromBlob(blob);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return blobToFile(blob, fileName, outputMimeType);

    let scale = Math.sqrt(targetBytes / blob.size);
    scale = Math.min(1, Math.max(minScale, scale));

    let bestBlob: Blob | null = null;

    for (let i = 0; i < maxIterations; i++) {
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));

        canvas.width = width;
        canvas.height = height;

        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(image, 0, 0, width, height);

        const resizedBlob: Blob = await new Promise(resolve => {
            if (outputMimeType === 'image/jpeg' || outputMimeType === 'image/webp') {
                canvas.toBlob(b => resolve(b as Blob), outputMimeType, quality);
            } else {
                canvas.toBlob(b => resolve(b as Blob), outputMimeType);
            }
        });

        bestBlob = resizedBlob;
        if (resizedBlob.size <= targetBytes) {
            return blobToFile(resizedBlob, fileName, outputMimeType);
        }

        scale = scale * Math.sqrt(targetBytes / Math.max(1, resizedBlob.size));
        scale = Math.max(minScale, Math.min(1, scale));
    }

    return bestBlob ? blobToFile(bestBlob, fileName, outputMimeType) : blobToFile(blob, fileName, outputMimeType);
};

export const resizeFileToMaxBytes = async (file: File, options?: ResizeOptions) => {
    return resizeBlobToMaxBytes(file, file.name, options);
};
